class CustomForm
{
  static NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7];
  static MAILING_FIELDS = [
    'custom#mailing_address_1',
    'custom#mailing_address_2',
    'custom#mailing_city',
    'custom#mailing_zip_code',
  ];
  static POLL_INTERVAL = 500;
  static MAX_POLLS = 60;

  constructor(formId)
  {
    this.formId = formId;
    this.shadow = null;
    this.nipInput = null;
    this.formEl = null;
    this.nipValid = false;
    this.termsAccepted = false;
    this.polls = 0;

    this.hideStyle = document.createElement('style');
    this.hideStyle.textContent =
      `[data-forms-id="forms-root-${formId}"] shopify-forms-embed { visibility: hidden !important; }`;
    document.head.appendChild(this.hideStyle);

    this.today = this.formatDate(new Date());
    this.poll();
  }

  formatDate(d)
  {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${y}-${m}-${day}`;
  }

  poll()
  {
    this.timer = setInterval(() => {
      this.polls++;
      const root = document.querySelector(
        `[data-forms-id="forms-root-${this.formId}"] shopify-forms-embed`
      );
      const shadow = root && root.shadowRoot;

      if (shadow && this.init(shadow)) {
        clearInterval(this.timer);
      } else if (this.polls >= CustomForm.MAX_POLLS) {
        clearInterval(this.timer);
        this.show();
      }
    }, CustomForm.POLL_INTERVAL);
  }

  show()
  {
    if (this.hideStyle && this.hideStyle.parentNode) {
      this.hideStyle.remove();
    }
  }

  init(shadow)
  {
    this.shadow = shadow;
    this.nipInput = shadow.querySelector('input#custom\\#nip');

    if (!this.nipInput) {
      return false;
    }

    try {
      this.formEl = this.nipInput.closest('form');
      this.injectStylesheet().then(() => this.show());
      this.addHeaders();
      this.groupFieldRows();
      this.setupNipValidation();
      this.movePhoneAfterBilling();
      this.setupMailingToggle();
      this.moveConsentsToBottom();
      this.addTermsCheckbox();
      this.addConsentText();
      this.wrapSubmitButton();
    } catch(e) {
      console.error(e);
      this.show();
    }

    return true;
  }

  getFieldContainer(fieldId)
  {
    const input = this.shadow.querySelector(`input#${CSS.escape(fieldId)}`);

    return input && input.closest('div[class*="formFieldContainer"]');
  }

  createHeader(text)
  {
    const h = document.createElement('h3');
    h.className = 'form-section-header';
    h.textContent = text;

    return h;
  }

  injectStylesheet()
  {
    if (!window.customForms?.cssUrl) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = window.customForms.cssUrl;
      link.onload = resolve;
      link.onerror = resolve;
      this.shadow.prepend(link);
    });
  }

  addHeaders()
  {
    const emailField = this.getFieldContainer('email');
    const salonField = this.getFieldContainer('custom#nazwa_salonu');

    if (emailField) {
      emailField.parentNode.insertBefore(this.createHeader(window.customForms.strings.yourData), emailField);
    }

    if (salonField) {
      salonField.parentNode.insertBefore(this.createHeader(window.customForms.strings.companyData), salonField);
    }
  }

  groupFieldRows()
  {
    const pairs = [
      ['first_name', 'last_name'],
      ['custom#billing_city', 'custom#billing_zip_code'],
      ['custom#mailing_city', 'custom#mailing_zip_code'],
    ];

    pairs.forEach(([id1, id2]) => {
      const c1 = this.getFieldContainer(id1);
      const c2 = this.getFieldContainer(id2);
      if (!c1 || !c2) return;

      const row = document.createElement('div');
      row.className = 'field-row';
      c1.parentNode.insertBefore(row, c1);
      row.appendChild(c1);
      row.appendChild(c2);
    });
  }

  setupNipValidation()
  {
    this.nipInput.addEventListener('blur', () => this.handleNipBlur());

    if (this.formEl) {
      this.formEl.addEventListener('submit', (e) => this.blockSubmit(e));
    }

    const submitBtn = this.shadow.querySelector('button[type="submit"]');

    if (submitBtn) {
      submitBtn.addEventListener('click', (e) => this.blockSubmit(e));
    }
  }

  validateChecksum(nip)
  {
    const digits = nip.split('').map(Number);
    const sum = CustomForm.NIP_WEIGHTS.reduce((acc, w, i) => acc + w * digits[i], 0);

    return sum % 11 === digits[9];
  }

  isValidNipFormat(nip)
  {
    return /^\d{10}$/.test(nip) && this.validateChecksum(nip);
  }

  async fetchNipValid(nip)
  {
    try {
      const url = `https://wl-api.mf.gov.pl/api/search/nip/${nip}?date=${this.today}`;
      const res = await fetch(url);
      const data = await res.json();

      return !!data.result?.subject;
    } catch {
      return false;
    }
  }

  async handleNipBlur()
  {
    const raw = this.nipInput.value.replace(/[\s-]/g, '');

    if (!raw) {
      this.nipValid = false;
      this.clearNipFeedback();
      return;
    }

    if (!this.isValidNipFormat(raw)) {
      this.nipValid = false;
      this.showNipError(window.customForms.strings.invalidNip);
      return;
    }

    const valid = await this.fetchNipValid(raw);
    this.nipValid = valid;

    if (valid) {
      this.showNipSuccess();
    } else {
      this.showNipError(window.customForms.strings.nipNotFound);
    }
  }

  showNipError(msg)
  {
    this.clearNipFeedback();
    this.nipInput.classList.add('nip-error');
    const p = document.createElement('p');
    p.className = 'nip-validation-msg';
    p.textContent = msg;
    this.nipInput.parentNode.appendChild(p);
  }

  showNipSuccess()
  {
    this.clearNipFeedback();
    this.nipInput.classList.add('nip-success');
  }

  clearNipFeedback()
  {
    this.nipInput.classList.remove('nip-error', 'nip-success');
    const prev = this.nipInput.parentNode.querySelector('.nip-validation-msg');

    if (prev) {
      prev.remove();
    }
  }

  blockSubmit(e)
  {
    let blocked = false;

    if (!this.nipValid) {
      this.showNipError(window.customForms.strings.correctNip);
      blocked = true;
    }

    if (this.termsCheckbox && !this.termsCheckbox.checked) {
      this.termsContainer.classList.add('terms-error');
      blocked = true;
    }

    if (blocked) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }

  movePhoneAfterBilling()
  {
    const phoneContainer = this.formEl.querySelector('[class*="formPhoneInputContainer"]');
    if (!phoneContainer) return;

    const billingZipRow = this.shadow.querySelector('.field-row:has(#' + CSS.escape('custom#billing_zip_code') + ')');
    const billingZip = this.getFieldContainer('custom#billing_zip_code');
    const insertAfter = billingZipRow || billingZip;

    if (insertAfter) {
      insertAfter.parentNode.insertBefore(phoneContainer, insertAfter.nextSibling);
    }
  }

  setupMailingToggle()
  {
    const mailingContainers = CustomForm.MAILING_FIELDS.map((id) => {
      const input = this.shadow.querySelector(`input#${CSS.escape(id)}`);
      return input && input.closest('div[class*="formFieldContainer"]');
    }).filter(Boolean);

    if (!mailingContainers.length) {
      return;
    }

    const mailingRow = this.shadow.querySelector('.field-row:has(#' + CSS.escape('custom#mailing_city') + ')');
    const allMailingEls = mailingRow
      ? [...mailingContainers.filter(el => !mailingRow.contains(el)), mailingRow]
      : mailingContainers;

    allMailingEls.forEach((el) => el.classList.add('mailing-field-hidden'));

    const header = this.createHeader(window.customForms.strings.mailingAddress);
    const firstMailing = mailingContainers[0];
    const insertBeforeEl = allMailingEls.find(el => el.parentNode === firstMailing.parentNode) || firstMailing;
    insertBeforeEl.parentNode.insertBefore(header, insertBeforeEl);

    const toggle = document.createElement('div');
    toggle.className = 'mailing-toggle-container';
    toggle.innerHTML =
      '<input type="checkbox" id="mailing-toggle-cb" checked>' +
      '<label for="mailing-toggle-cb">' + window.customForms.strings.mailingAddressSame + '</label>';

    header.after(toggle);

    toggle.querySelector('input').addEventListener('change', (e) => {
      allMailingEls.forEach((el) => {
        el.classList.toggle('mailing-field-hidden', e.target.checked);
      });
    });
  }

  addTermsCheckbox()
  {
    const submitBtn = this.formEl.querySelector('button[type="submit"]');
    if (!submitBtn) return;

    const firstConsent = this.formEl.querySelector('[class*="consentCheckboxContainer"]');
    const insertBefore = firstConsent || submitBtn;

    const container = document.createElement('div');
    container.className = 'terms-checkbox-container';
    const s = window.customForms?.strings || {};
    container.innerHTML =
      '<label class="terms-checkbox-label">' +
        '<div class="terms-checkbox-wrapper">' +
          '<input type="checkbox" id="terms-cb">' +
        '</div>' +
        '<span>' + (s.termsPrefix || 'Rejestrując się, akceptuję') + ' ' +
          '<a href="' + (window.registerStrings?.privacyPolicyUrl || '/policies/privacy-policy') + '" target="_blank" rel="noopener">' + (s.termsPrivacyPolicy || 'Politykę prywatności') + '</a>' +
          ' ' + (s.termsAnd || 'oraz') + ' ' +
          '<a href="' + (window.registerStrings?.termsOfServiceUrl || '/policies/terms-of-service') + '" target="_blank" rel="noopener">' + (s.termsOfService || 'Regulamin serwisu') + '</a>' +
          '<span class="terms-required">*</span>' +
        '</span>' +
      '</label>';

    insertBefore.parentNode.insertBefore(container, insertBefore);

    this.termsContainer = container;
    this.termsCheckbox = container.querySelector('#terms-cb');
    this.termsCheckbox.addEventListener('change', () => {
      this.termsContainer.classList.remove('terms-error');
    });
  }

  moveConsentsToBottom()
  {
    if (!this.formEl) {
      return;
    }

    const consents = this.formEl.querySelectorAll('[class*="consentCheckboxContainer"]');
    const submitBtn = this.formEl.querySelector('button[type="submit"]');

    if (!submitBtn) {
      return;
    }

    consents.forEach((consent) => {
      submitBtn.parentNode.insertBefore(consent, submitBtn);
    });
  }

  addConsentText()
  {
    const emailConsent = this.formEl.querySelector('[class*="consentCheckboxContainer"]:has(fieldset[name="emailConsent"])');
    const phoneConsent = this.formEl.querySelector('[class*="consentCheckboxContainer"]:has(fieldset[name="phoneConsent"])');

    if (emailConsent && window.registerStrings?.emailConsent) {
      const text = document.createElement('div');
      text.className = 'custom-form-consent-text';
      text.innerHTML = `<div class="custom-form-consent-text__email">${window.registerStrings.emailConsent}</div>`;
      emailConsent.appendChild(text);
    }

    if (phoneConsent && window.registerStrings?.phoneConsent) {
      const text = document.createElement('div');
      text.className = 'custom-form-consent-text';
      text.innerHTML = `<div class="custom-form-consent-text__phone">${window.registerStrings.phoneConsent}</div>`;
      phoneConsent.appendChild(text);
    }
  }

  wrapSubmitButton()
  {
    const btn = this.shadow.querySelector('button[type="submit"]');
    if (!btn) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'submit-button-wrapper';
    btn.parentNode.insertBefore(wrapper, btn);
    wrapper.appendChild(btn);
  }
}

window.addEventListener('DOMContentLoaded', () => {
  window.registerForm = new CustomForm(929044);
});
